import './index.css';
import { IProps } from './index.d';
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { throttle } from '../utils/throttle';
import CodeMirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
require('codemirror/addon/dialog/dialog');
import 'codemirror/addon/dialog/dialog.css';
require('codemirror/addon/edit/closebrackets');
require('codemirror/addon/edit/matchbrackets');
require('codemirror/addon/scroll/simplescrollbars');
import 'codemirror/addon/scroll/simplescrollbars.css';
require('codemirror/addon/hint/show-hint');
import 'codemirror/addon/hint/show-hint.css';
require('codemirror/addon/display/placeholder');
import defineMode from './mode';
import generatePinHint from './hints/generator';
import addTooltipEventListener from './events/tooltip';
const ExpressionInference = require('../ast/index').inference;
import replaceTextWithElement from './markers';
import StupidDatePicker from './components/stupid-date-picker';
import StupiddDatePickerStore from './components/stupid-date-picker/store';
import StupidDropdownMenu, { IMenu } from './components/stupid-dropdown-menu';
import StupidDropdownSelect from './components/stupid-dropdown-select';
import StupidDropdownSelectStore, { TSelectFunction, TSelectValue, IPosition } from './components/stupid-dropdown-select/store';
import { isEnumOrBooleanExpression } from './hints/trigger/enum-boolean';
import { getCurrentMemberGradeId } from './hints/trigger/current-member-grade';
import generateNormalHint from './hints/trigger/normal';
import { matchFqnPathHint } from './hints/trigger/fqn-path';
import { matchModelPathHint } from './hints/trigger/model-path';
import { transformFunctionsToObject, transformDeclarationsToObject } from '../helpers/transforms';
import { operators, DefaultOperatorReplacements, DefaultCustomProperties, DefaultPromptMessages } from './config';


// 表达式实例计数器
let instanceCounter = 0;

type THintFunction = (cm: CodeMirror.Editor, options?: any) => void;

export default class ExpressEditor extends React.Component<IProps> {
  public static defaultProps = {
    allowContextMenu: true
  };

  private instance!: CodeMirror.Editor;

  private instanceName = `expression-editor-${++instanceCounter}`;

  private codeMirrorRef!: React.RefObject<HTMLDivElement>;

  public normalHint?: THintFunction;
  // public currentMemberGradeHierarchyHint?: THintFunction;
  // public currentMemberPointAccountHint?: THintFunction;

  public state = {
    dropdownMenu: { position: { left: 0, top: 0 }, visible: false },
    datePickerStore: new StupiddDatePickerStore((newState: any) => {
      this.setState({ datePickerStore: Object.assign(this.state.datePickerStore, newState) });
    }),
    stupidDropdownSelectStore: new StupidDropdownSelectStore((newState: any) => {
      this.setState({ stupidDropdownSelectStore: Object.assign(this.state.stupidDropdownSelectStore, newState) });
    })
  };

  public componentWillMount() {
    this.codeMirrorRef = React.createRef();
  }

  public componentDidMount() {
    const { onRef } = this.props;
    if (onRef) {
      onRef(this);
    }

    this.initCodeMirror();

    // 如果节点被设置了`editor-tooltip`属性，则`hover`时显示错误信息
    addTooltipEventListener(this.codeMirrorRef.current!);

  }


  public showHint(hint: THintFunction) {
    (this.instance as any).showHint({ hint });
  }


  public showDropdownSelector({ isMultiple, position, select, options }: {
    isMultiple: boolean,
    position: IPosition,
    select: TSelectFunction,
    options: Array<{ name: string, value: string }>
  }) {
    const value = isMultiple ? [] : undefined;
    this.state.stupidDropdownSelectStore.open({ isMultiple, options, position, select, value });
  }


  public showCurrentMemberGradeIdSelector(config: {
    gradeId: number,
    isMultiple: boolean,
    position: IPosition,
    select: TSelectFunction
  }) {
    const { gradeId, isMultiple, position, select } = config;
    const { gradeHierarchies = [] } = this.props;

    const grade = gradeHierarchies.find(item => item.id === gradeId);
    if (!grade) { return; }

    const options = grade.grades.map(item => {
      return { value: `Id(${item.id})`, name: item.name };
    });
    const value = isMultiple ? [] : undefined;
    this.state.stupidDropdownSelectStore.open({ isMultiple, options, position, select, value });
  }


  // 替换表达式中的属性节点
  private replaceTextWithElement() {
    const {
      fqn,
      customProperties = DefaultCustomProperties,
      operatorReplacements = DefaultOperatorReplacements,
      promptMessages = DefaultPromptMessages,
      pointAccounts = [],
      gradeHierarchies = [],
      showDescription = false
    } = this.props;

    const { functions, declarations, } = this.parseConfig();

    replaceTextWithElement({
      fqn,
      cm: this.instance,
      functions,
      declarations,
      customProperties,
      operatorReplacements,
      promptMessages,
      datePickerStore: this.state.datePickerStore,
      pointAccounts,
      gradeHierarchies,
      showDescription
    });
  }

  private parseConfig() {
    const {
      models = [],
      placeholders = [],
      customProperties = DefaultCustomProperties,
      customHints = [],
      proxies = []
    } = this.props;


    const functions = transformFunctionsToObject(this.props.functions);
    const declarations = transformDeclarationsToObject(this.props.declarations);
    const hintDeclarations = (this.props.hintDeclarations || []).map(item => {
      const declaration = declarations.find(v => v.code === item);
      if (!declaration) {
        // tslint:disable-next-line
        console.error(`${item} in hintDeclarations, but not exist in declarations`);
      }
      return { code: item, name: declaration ? declaration.name : item };
    });

    return {
      models,
      customHints,
      placeholders,
      customProperties,
      functions,
      hintFunctions: functions.filter(item => item.hint !== false),
      declarations,
      hintDeclarations,
      proxies
    };
  }

  private initCodeMirror = () => {
    const { value, disabled, fqn, placeholder = '',
      operatorReplacements = DefaultOperatorReplacements
    } = this.props;

    const { models,
      functions, hintFunctions,
      declarations, hintDeclarations,
      customProperties, customHints, proxies } = this.parseConfig();


    defineMode({
      name: this.instanceName,
      fqn,
      functions,
      declarations,
      proxies,
      getPlaceholders: () => this.props.placeholders || []
    });

    this.normalHint = generateNormalHint({
      operators,
      functions: hintFunctions,
      declarations: hintDeclarations,
      customProperties: customProperties.filter(v => !v.disabled),
      operatorReplacements,
      models,
      customHints
    });

    // // 当前会员等级提示
    // this.currentMemberGradeHierarchyHint = currentMemberHintGenerator({
    //   hints: gradeHierarchies.map(item => ({ text: item.id + '', displayText: item.name }))
    // });
    // // 当前会员积分账户提示
    // this.currentMemberPointAccountHint = currentMemberHintGenerator({
    //   hints: pointAccounts.map(item => ({ text: item.id + '', displayText: item.name }))
    // });

    this.instance = CodeMirror(this.codeMirrorRef.current!, {
      value,
      placeholder,
      showHint: true,
      hintOptions: {
        completeSingle: false,
        alignWithWord: false
      },
      readOnly: disabled,
      lineWrapping: true,
      autoCloseBrackets: true,
      mode: name,
      scrollbarStyle: 'simple',
      matchBrackets: true
    } as CodeMirror.EditorConfiguration);


    this.replaceTextWithElement();


    // --------------------------------------------
    // 右键插入积分账户和等级体系
    const codeMirrorDiv = findDOMNode(this.codeMirrorRef.current!) as HTMLDivElement;
    codeMirrorDiv.addEventListener('contextmenu', (evt: MouseEvent) => {
      const { allowContextMenu, disabled } = this.props;
      if (!disabled && allowContextMenu) {
        evt.preventDefault();
        const { clientX: left, clientY: top } = (evt as MouseEvent);
        this.setState({
          dropdownMenu: { visible: true, position: { left, top } }
        });
      }
    }, false);
    // --------------------------------------------


    // 选择器打开状态时编辑器不能输入
    this.instance.on('beforeChange', (_R, changeObj) => {
      if (this.state.stupidDropdownSelectStore.visible) {
        changeObj.cancel();
      }
    });


    this.instance.on('change', (cm, changeObj) => {

      // 替换表达式中的属性节点
      this.replaceTextWithElement();

      const expression = cm.getValue();

      this.onEditorContentChange(changeObj, expression);

    });
  }

  private inference(input: string) {
    try {
      const referenced = ExpressionInference(input, this.getIAstOptions());
      return { referenced };
      // return referenced ? (referenced.type + (referenced.isArray ? 'Array' : '')) : undefined;
    } catch (error) {
      return { error };
    }
  }

  private getIAstOptions() {

    const { fqn,
      pointAccounts = [],
      gradeHierarchies = [],
      presetPaths = {},
      promptMessages = DefaultPromptMessages,
      proxies
    } = this.props;

    const { models, placeholders, functions, declarations, customProperties } = this.parseConfig();

    return {
      fqn,
      models,
      proxies,
      presetPaths,
      placeholders,
      functions,
      declarations,
      customProperties,
      pointAccounts,
      gradeHierarchies,
      promptMessages
    };
  }

  private onEditorContentChange = throttle((changeObj, expression) => {
    const {
      fqn,
      models = [],
      gradeHierarchies = [],
      customProperties = DefaultCustomProperties,
      onChange
    } = this.props;

    if (onChange) {
      const { referenced, error } = this.inference(expression);
      if (error) {
        onChange(expression, undefined, error);
      } else {
        const inferencedType = referenced ? (referenced.type + (referenced.isArray ? 'Array' : '')) : undefined;
        onChange(expression, inferencedType);
      }
    }

    if (!['+input', '+delete', '*compose'].includes(changeObj.origin!)) {
      return;
    }

    const pos = (this.instance as any).getCursor();

    const matchedEnumExpression = changeObj.origin !== '+delete' && isEnumOrBooleanExpression({
      editor: this.instance, pos, customProperties, fqn, models
    });
    if (matchedEnumExpression) {
      const { left, top } = this.instance.cursorCoords(true, 'window');
      this.showDropdownSelector({
        isMultiple: matchedEnumExpression.isMultiple,
        options: matchedEnumExpression.options,
        position: { left, top },
        select: (value: TSelectValue) => {
          const replacement = typeof value === 'string' ? value :
            value.length ? `[${value.join(', ')}]` : '';
          if (replacement) {
            this.insertValue(replacement, 0, pos);
          }
        }
      });
    }


    // =====================================
    // CURRENT_MEMBER_GRADE、CURRENT_MEMBER_POINT两个函数需要特殊处理
    // const currentMemberFuncName = getCurrentMemberFunction(pos, cm);
    // if (currentMemberFuncName) {
    //   return this.showHint(({
    //     CURRENT_MEMBER_GRADE: this.currentMemberGradeHierarchyHint!,
    //     CURRENT_MEMBER_POINT: this.currentMemberPointAccountHint!
    //   } as { [key: string]: THintFunction })[currentMemberFuncName]);
    // };

    const gradeHierarchyIds = gradeHierarchies.map(item => item.id);
    const result = changeObj.origin !== '+delete' && getCurrentMemberGradeId(this.instance, pos, gradeHierarchyIds, customProperties);
    if (result) {
      const { id: gradeId, isMultiple } = result;
      const { left, top } = this.instance.cursorCoords(true, 'window');
      const position = { left, top };
      document.body.focus();
      return this.showCurrentMemberGradeIdSelector({
        gradeId, isMultiple, position,
        select: (value: TSelectValue) => {
          const replacement = typeof value === 'string' ? value :
            value.length ? `[${value.join(', ')}]` : '';
          if (replacement) {
            this.insertValue(replacement, 0, pos);
          }
        }
      });
    }
    if (this.state.stupidDropdownSelectStore.visible && !matchedEnumExpression && !result) {
      this.state.stupidDropdownSelectStore.close();
    }
    // =====================================

    // 关闭智能提示
    this.showHint(generatePinHint({ hints: [], beingReplaced: 0 }));

    // 模型+路径 模式：DM.JD.Order(orders.id)
    const modelPathHints = matchModelPathHint({ editor: this.instance, position: pos, models });
    if (modelPathHints) {
      return this.showHint(generatePinHint(modelPathHints));
    }

    // 主体模型模式：this.orders.id
    const fqnPathHints = matchFqnPathHint({ editor: this.instance, position: pos, fqn: fqn! });
    if (fqnPathHints) {
      return this.showHint(generatePinHint(fqnPathHints));
    }

    this.showHint(this.normalHint!);
  }, 24);

  public insertValue = (
    content: string,
    cursorOffset = 0,
    position = this.instance.getDoc().getCursor()
  ) => {
    const cm = this.instance;
    const doc = cm.getDoc();
    doc.replaceRange(content, position);
    const currentCursor = doc.getCursor();
    // 编辑器自动获取焦点
    cm.focus();
    // 设置光标为刚插入函数的参数位置
    doc.setCursor({ ...currentCursor, ch: currentCursor.ch + cursorOffset });
  }

  private generateDropdownMenus() {
    const menus: IMenu[] = [];
    const { pointAccounts, gradeHierarchies } = this.props;
    if (pointAccounts && pointAccounts.length > 0) {
      menus.push({
        name: '积分账户',
        children: pointAccounts.filter(item => !item.disabled).map(item => ({
          name: item.name,
          value: `CURRENT_MEMBER_POINT(${item.id})`
        }))
      });
    }
    if (gradeHierarchies && gradeHierarchies.length > 0) {
      menus.push({
        name: '等级体系',
        children: gradeHierarchies.filter(item => !item.disabled).map(item => ({
          name: item.name,
          value: `CURRENT_MEMBER_GRADE(${item.id})`
        }))
      });
    }
    return menus;
  }

  public render() {
    const { className, disabled, height = 150 } = this.props;
    const classnames = ['loyalty-lp4-editor'];
    if (className) {
      classnames.push(className);
    }
    if (disabled) {
      classnames.push('disabled');
    }

    return (
      <div
        className={classnames.join(' ')}
        style={{ position: 'relative', marginBottom: 10 }}
      >

        <div style={{ margin: '0 auto', height }} ref={this.codeMirrorRef} />

        <StupidDropdownSelect store={this.state.stupidDropdownSelectStore} />

        <StupidDropdownMenu
          visible={this.state.dropdownMenu.visible}
          menus={this.generateDropdownMenus()}
          position={this.state.dropdownMenu.position}
          close={() => this.setState({ dropdownMenu: Object.assign(this.state.dropdownMenu, { visible: false }) })}
          insert={this.insertValue}
        />
        <StupidDatePicker store={this.state.datePickerStore} />
      </div>
    );
  }
}
